import clsx from 'clsx';
import { CheckCircle, CircleAlert } from 'lucide-react';
import type { PropsWithChildren } from 'react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@doger/ui/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@doger/ui/components/ui/dialog';
import { Skeleton } from '@doger/ui/components/ui/skeleton';
import { useJWT, useUser } from '@doger/core/modules/auth';
import { CookieSettings } from '@doger/core/shared/cookies';

import { LogAmplitudeEventOnMount } from '@doger/chat/shared/analytics';
import { useCookieConsent } from '@doger/chat/shared/analytics/analytics.provider';

import { useAuth } from '../auth.context';
import styles from './user-profile-details-modal.module.scss';

export const UserProfileDetailsModal = memo(() => {
  const jwt = useJWT();
  const { t } = useTranslation();
  const { onPreferences } = useCookieConsent();

  const {
    isUserProfileDetailsModalOpen,
    setIsUserProfileDetailsModalOpen,
    setIsEmailConfirmationModalOpen,
    setIsWhitelistModalOpen,
  } = useAuth();

  const handleConfirmEmail = () => {
    setIsUserProfileDetailsModalOpen(false);
    setIsEmailConfirmationModalOpen(true);
  };

  const handleProvideUsecase = () => {
    setIsUserProfileDetailsModalOpen(false);
    setIsWhitelistModalOpen(true);
  };

  if (!jwt) {
    return null;
  }

  return (
    <Dialog
      open={isUserProfileDetailsModalOpen}
      onOpenChange={setIsUserProfileDetailsModalOpen}
    >
      <DialogContent className={styles.modal}>
        <LogAmplitudeEventOnMount event='Profile Modal Viewed' />

        <DialogHeader>
          <DialogTitle>{t('profile.modal.title')}</DialogTitle>
        </DialogHeader>

        <DialogBody className={styles.body}>
          <ModalSection>
            <NameRow />

            <EmailRow email={jwt.email} />

            <StatusRow isConfirmed={jwt.isConfirmed} />

            <CreatedAtRow />
          </ModalSection>

          <ModalSection>
            <CookieSettings onPreferencesChange={onPreferences} />
          </ModalSection>

          {!jwt.isConfirmed && (
            <div className={styles.banner}>
              <div className={styles.bannerContent}>
                <div className={styles.bannerText}>
                  <h4 className={styles.bannerTitle}>
                    {t('profile.modal.banner.confirm-email.title')}
                  </h4>

                  <p className={styles.bannerDescription}>
                    {t('profile.modal.banner.confirm-email.description')}
                  </p>
                </div>
              </div>

              <Button
                onClick={handleConfirmEmail}
                variant='contained'
                size='md'
                palette='primary'
              >
                {t('profile.modal.banner.confirm-email.action')}
              </Button>
            </div>
          )}

          {!jwt.completedProfile && (
            <div className={styles.banner}>
              <div className={styles.bannerContent}>
                <div className={styles.bannerText}>
                  <h4 className={styles.bannerTitle}>
                    {t('whitelist.banner.title')}
                  </h4>

                  <p className={styles.bannerDescription}>
                    {t('whitelist.banner.description')}
                  </p>
                </div>
              </div>

              <Button
                onClick={handleProvideUsecase}
                variant='contained'
                size='md'
                palette='primary'
              >
                {t('whitelist.banner.action')}
              </Button>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
});

const ModalSection = memo(({ children }: PropsWithChildren) => (
  <div className={styles.section}>{children}</div>
));

const NameRow = memo(() => {
  const { t } = useTranslation();
  const { data: user, isLoading } = useUser();

  if (isLoading || !user) {
    return <Skeleton className={styles.skeleton} />;
  }

  return (
    <div className={styles.infoItem}>
      <span className={styles.infoValue}>
        {!!user.firstName && !!user.lastName
          ? `${user.firstName} ${user.lastName}`
          : t('profile.modal.fields.name.fallback')}
      </span>

      <span className={styles.infoLabel}>
        {t('profile.modal.fields.name.label')}
      </span>
    </div>
  );
});

const EmailRow = memo(({ email }: { email: string }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.infoItem}>
      <span className={styles.infoValue}>{email}</span>

      <span className={styles.infoLabel}>
        {t('profile.modal.fields.email.label')}
      </span>
    </div>
  );
});

const StatusRow = memo(({ isConfirmed }: { isConfirmed: boolean }) => {
  const { t } = useTranslation();

  return (
    <>
      {isConfirmed ? (
        <div className={styles.infoItem}>
          <div className={clsx(styles.statusContainer, styles.statusConfirmed)}>
            <CheckCircle className={styles.statusIcon} />

            <span>{t('profile.modal.fields.status.confirmed')}</span>
          </div>

          <span className={styles.infoLabel}>
            {t('profile.modal.fields.status.label')}
          </span>
        </div>
      ) : (
        <div className={styles.infoItem}>
          <div
            className={clsx(styles.statusContainer, styles.statusNotConfirmed)}
          >
            <CircleAlert className={styles.statusIcon} />

            <span>{t('profile.modal.fields.status.notConfirmed')}</span>
          </div>

          <span className={styles.infoLabel}>
            {t('profile.modal.fields.status.label')}
          </span>
        </div>
      )}
    </>
  );
});

const CreatedAtRow = memo(() => {
  const { t, i18n } = useTranslation();
  const { data: user, isLoading } = useUser();
  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    [i18n.language]
  );

  if (isLoading || !user) {
    return <Skeleton className={styles.skeleton} />;
  }

  return (
    <div className={styles.infoItem}>
      <span className={styles.infoValue}>
        {formatter.format(new Date(user.createdAt))}
      </span>

      <span className={styles.infoLabel}>
        {t('profile.modal.fields.createdAt.label')}
      </span>
    </div>
  );
});

UserProfileDetailsModal.displayName = 'UserProfileDetailsModal';
